import { placeActions } from '@/actions';
import { ROUTE_BUILDER } from '@/constants/pages';
import { setBookingStartingPoint } from '@/helpers';
import { getSearchResultsPageHeadingProps } from '@/helpers/seoTs';
import { slug } from '@/helpers/urlTs';
import { DEFAULT_SEARCH_KEYWORD } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug, SearchType } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { _t } from '@/locale';
import { DealsSearchState } from '@/reducers/dealsSearchReducer';
import { Tracking } from '@/types/analytics';
import { UrlSearchState } from '@/types/state/search';
import { Dispatch } from 'react';
import { AnyAction } from 'redux';
import { CroLabelProps } from './components/CroLabel';
import MPServiceCard from './components/MPServiceCard/MPServiceCard';
import { trackSerpCardClick } from './tracking';
import { DealSearchItem, SistaminutenSearchItem } from './types/dealSearch';
import { DealCroLabel } from './types/shared';

type Breadcrumb = { title: string; url: string };

export function getDealsSerpBreadcrumbs(q?: string, location?: string, type?: SearchType): Breadcrumb[] {
  const indexableKeyword = getKeywordWithSlug(q);
  const indexableLocation = getLocationWithSlug(location);

  const items: Breadcrumb[] = [
    { title: _t('home'), url: '/' },
    { title: 'Deals', url: ROUTE_BUILDER.dealsLanding() },
  ];

  if (indexableKeyword?.slug && indexableKeyword.slug !== DEFAULT_SEARCH_KEYWORD) {
    items.push(buildDealsLeafCrumb(type, indexableKeyword, getLocationWithSlug()));
  }

  if (indexableLocation?.slug) {
    items.push({
      title: indexableLocation.label,
      url: ROUTE_BUILDER.dealsSerpPreview(indexableKeyword?.slug ?? DEFAULT_SEARCH_KEYWORD, indexableLocation.slug),
    });
  }

  return items;
}

function buildDealsLeafCrumb(
  type: SearchType,
  keyword: { label: string; slug: string },
  location: { label: string; slug: string } | undefined,
): Breadcrumb {
  const locationSlug = location?.slug;

  switch (type) {
    case 'deals':
      return {
        title: keyword.label,
        url: ROUTE_BUILDER.dealsSerp(keyword.slug, locationSlug),
      };
    case 'brand_deals':
      return {
        title: keyword.label,
        url: ROUTE_BUILDER.brandDealsSerp(keyword.slug, locationSlug),
      };
    case 'deals_sistaminuten':
      return {
        title: keyword.label,
        url: ROUTE_BUILDER.sistaMinutenSerp(keyword.slug, locationSlug),
      };
    case 'deals_preview':
    default:
      return {
        title: keyword.label,
        url: ROUTE_BUILDER.dealsSerpPreview(keyword.slug, locationSlug),
      };
  }
}

export function getDealsSearchHeadingProps(searchManager: BaseSearchManagerOutput): {
  keyword?: string;
  location?: string;
  description?: string;
} {
  let { keyword, location, description } = getSearchResultsPageHeadingProps(searchManager);

  const hasKeyword = !!searchManager.q;

  if (searchManager.type === 'deals_preview') {
    keyword = hasKeyword ? _t('DealsWithDiscount', { keyword, interpolation: { escapeValue: false } }) : _t('Deals');
  } else if (searchManager.type === 'deals') {
    keyword = hasKeyword ? _t('DealsOn', { keyword, interpolation: { escapeValue: false } }) : _t('Deals');
  } else if (searchManager.type === 'brand_deals') {
    keyword = _t('DealsWith', { keyword, interpolation: { escapeValue: false } });
  } else if (searchManager.type === 'deals_sistaminuten') {
    keyword = hasKeyword ? _t('SistaMinutenOn', { keyword, interpolation: { escapeValue: false } }) : 'Sista Minuten';
  }

  return { keyword, location, description };
}

export const renderSistaminutenCard = ({
  item,
  searchParams,
  dispatch,
  onClick,
}: {
  item: SistaminutenSearchItem;
  dispatch: Dispatch<AnyAction>;
  searchParams?: UrlSearchState;
  onClick?: (item: SistaminutenSearchItem) => void;
}) => {
  return (
    <div
      key={item.id}
      onMouseEnter={() => dispatch(placeActions.setHover(String(item.id)))}
      onMouseLeave={() => dispatch(placeActions.removeHover())}>
      <MPServiceCard
        type="sistaminuten"
        to={{
          pathname: `/places/${item.slug}-${item.id}`,
          search: '?sistaminuten=1',
          state: { fromList: true, searchParams: searchParams },
        }}
        title={item.name}
        address={buildPlaceAddress(item.address)}
        imageUrl={item.images?.[0] ?? item.profileImageURL}
        merchantLogoUrl={item.profileImageURL}
        reviewRating={item.rating?.score}
        ratingCount={item.rating?.count}
        labels={[{ label: _t('UpToDiscountPercentage', { discount: item.lastMinuteDiscount }), variant: 'discount' }]}
        matchedServicesCount={item.matchedServices?.length}
        bookableServicesCountLabel={item.bookableServicesCountLabel}
        onClick={() => onClick(item)}
        croLabel={mapCroLabelProps({ type: 'smt' })}
      />
    </div>
  );
};

export const mapCroLabelProps = (item: DealCroLabel): CroLabelProps | null => {
  const common: Pick<CroLabelProps, 'showIcon'> = { showIcon: true };
  if (!item) return null;

  switch (item.type) {
    case 'nextAvailableTime':
      return {
        type: 'nextAvailableTime',
        label: item.value,
        ...common,
      };
    case 'bookingWindow':
      return {
        type: 'bookingWindow',
        label: item.value,
        ...common,
      };
    case 'boughtServices':
      return {
        type: 'bought',
        label: _t('croLabel.bought', { amount: item.value }),
        ...common,
      };
    case 'lowStock': {
      return {
        type: 'lowStock',
        label: _t('croLabel.lowStock', { amount: item.value }),
        ...common,
      };
    }
    case 'productDiscount': {
      return {
        type: 'productDiscount',
        label: _t('croLabel.productDiscount'),
        ...common,
      };
    }
    case 'smt': {
      return {
        type: 'smt',
        label: _t('croLabel.smt'),
        ...common,
      };
    }

    default:
      return null;
  }
};

export const buildPlaceAddress = (address?: { street?: string; city?: string }): string | undefined => {
  const parts = [address?.street, address?.city].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : undefined;
};

export const renderDealCard = ({
  item,
  dispatch,
  onClick,
}: {
  item: DealSearchItem;
  searchParams?: UrlSearchState;
  dispatch: Dispatch<AnyAction>;
  onClick?: (item: DealSearchItem) => void;
}) => {
  const croLabel = mapCroLabelProps(item.croLabels?.[0]);
  return (
    <div
      key={item.id}
      onMouseEnter={() => dispatch(placeActions.setHover(String(item.place.id)))}
      onMouseLeave={() => dispatch(placeActions.removeHover())}>
      <MPServiceCard
        type="deal"
        title={item.name}
        subtitle={item.place.name}
        address={buildPlaceAddress(item.place.address)}
        price={item.priceLabel}
        ratingCount={item.place?.rating?.count}
        reviewRating={item.place?.rating?.score}
        originalPrice={item.originalPriceLabel}
        imageUrl={item.imageUrl}
        merchantLogoUrl={item.place?.profileImageURL}
        brandProductImageUrl={item.brand?.productImageUrl}
        duration={item.durationLabel}
        to={{ pathname: ROUTE_BUILDER.dealDetails(`${slug(item.name)}-${item.id}`) }}
        croLabel={croLabel}
        labels={(item.discountLabels || []).map((discount) => ({
          label: discount.label,
          variant: discount.type,
        }))}
        onClick={() => onClick(item)}
      />
    </div>
  );
};

export function getBrandSerpHeaderImage(
  logoUrl: string | undefined,
  isMobileView: boolean,
): { src: string; height: number; width: number } | null {
  if (!logoUrl) return null;
  const height = isMobileView ? 40 : 64;
  const width = undefined; // Let the image define its own width to maintain aspect ratio
  return { src: logoUrl, height, width };
}

export function getSerpHeaderImage(
  type: SearchType,
  isMobileView: boolean,
): {
  src: string;
  height: number;
  width: number;
} | null {
  if (type === 'deals_preview') {
    const src = isMobileView
      ? '/images/DealsCombinedIllustrationSmall.png'
      : '/images/DealsCombinedIllustrationLarge.png';
    const height = isMobileView ? 78 : 178;
    const width = isMobileView ? 88 : 200;
    return { src, height, width };
  }

  if (type === 'deals') {
    const src = isMobileView ? '/images/DealsIllustrationSmall.png' : '/images/DealsIllustrationLarge.png';
    const height = isMobileView ? 78 : 178;
    const width = isMobileView ? 72 : 164;
    return { src, height, width };
  }

  if (type === 'deals_sistaminuten') {
    const src = isMobileView ? '/images/SMTIllustrationSmall.png' : '/images/SMTIllustrationLarge.png';
    const height = isMobileView ? 71 : 178;
    const width = isMobileView ? 64 : 160;
    return { src, height, width };
  }

  return null;
}

export function createSerpCardClickHandlers(
  searchManager: BaseSearchManagerOutput,
  state: DealsSearchState,
  screenName: Tracking.ScreenName,
  onBeforeNavigate?: () => void,
) {
  return {
    onDealClick: (item: DealSearchItem) => {
      onBeforeNavigate?.();
      trackSerpCardClick({ searchManager, state, screenName, item: { type: 'deal', data: item } });
      setBookingStartingPoint(screenName, searchManager.q);
    },
    onMerchantClick: (item: SistaminutenSearchItem) => {
      onBeforeNavigate?.();
      trackSerpCardClick({
        searchManager,
        state,
        screenName,
        item: { type: 'sistaminuten', data: item },
      });
      setBookingStartingPoint(screenName, searchManager.q);
    },
  };
}
